import * as React from 'react';
import { ChangeEvent, useState } from 'react';
import { Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ITotalPlatforms } from '../../../../../../../../model/platform/ITotalPlatforms';
import Counter from '../../../../../../../../shared/components/Counter';
import { CustomImageHandler } from '../../../../../../../../shared/components/CustomImageHandler';
import {
    calculateAvgReach,
    checkNumberPosts,
    getChildOfferPricePerPlatform,
    getCpmValue,
    getPostsPerPlatform,
} from '../../../../../../../../shared/functions/Functions';
import { TrashIcon } from '../../../../../../../../shared/Icons';
import { setOfferItems } from '../../../../../../../../store/brand/childOfferBrandSlice';
import { IAllStates } from '../../../../../../../../store/rootReducer';
import { numberFormatter } from '../../../../../../../../utils/helpers/numberFormater';
import { priceSplitter } from '../../../../../../../../utils/helpers/priceSplitter';
import Flag from '../../../../../../../../shared/components/flag/Flag';

interface IPlatformAccordionProps {
    platform: ITotalPlatforms;
}

const ChildOfferPlatformItem = ({ platform }: IPlatformAccordionProps) => {
    const { initialChildOffer, isCharity, isEditable } = useSelector((state: IAllStates) => state.childOfferBrand);
    const [expended, setExpended] = useState(isEditable);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    function deleteOfferItem(socialPlatformId: number) {
        const initialOfferItemCopy = [...initialChildOffer.offerItems];
        const selectedOfferItem = initialOfferItemCopy.find(item => item.socialPlatformId === socialPlatformId);
        const index = selectedOfferItem ? initialOfferItemCopy.indexOf(selectedOfferItem) : 0;
        initialOfferItemCopy[index] = { ...initialOfferItemCopy[index], nbPosts: 0, price: 0 };
        dispatch(setOfferItems(initialOfferItemCopy));
    }

    function changeHandlerOfferItem(
        socialPlatformId: number,
        event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        nbPosts?: number,
        mainPlatformId?: number
    ) {
        // const value = event && Number(event.target.value);
        const initialOfferItemCopy = [...initialChildOffer.offerItems];
        const selectedOfferItem = initialOfferItemCopy.find(item => item.socialPlatformId === socialPlatformId);
        const index = selectedOfferItem ? initialOfferItemCopy.indexOf(selectedOfferItem) : 0;
        if (selectedOfferItem) {
            if (nbPosts === 0) {
                deleteOfferItem(socialPlatformId);
            } else {
                initialOfferItemCopy[index] = {
                    ...initialOfferItemCopy[index],
                    price: nbPosts === 0 ? 0 : (initialOfferItemCopy[index].oldPrice ?? 0) * (nbPosts ?? 0),
                    nbPosts: nbPosts === undefined ? initialOfferItemCopy[index].nbPosts : nbPosts,
                };
                dispatch(setOfferItems(initialOfferItemCopy));
            }
        }
    }

    const isStrikeTrough = getPostsPerPlatform(initialChildOffer.offerItems, platform.id) === 0;

    return (
        <>
            <tr className="cursor-pointer" onClick={() => setExpended(!expended)}>
                <td>
                    <div className="d-flex align-items-center">
                        <CustomImageHandler altTag={platform?.name ?? ''} photoPath={platform?.path ?? null} />
                        <span
                            className={`text-primary font-weight-bold pl-1 mb-0 ${
                                isStrikeTrough ? 'text-line-through' : ''
                            }`}
                        >
                            x
                            {isEditable
                                ? getPostsPerPlatform(initialChildOffer.offerItems, platform.id)
                                : platform.nbPostsTotal}
                        </span>
                        {expended ? (
                            <Image className="ml-2" alt="image-down" src="/icons/img_arrow_up.svg" />
                        ) : (
                            <Image className="ml-2" alt="image-down" src="/icons/img_arrow_down.svg" />
                        )}
                    </div>
                </td>
                <td className="text-center">
                    <p className={isStrikeTrough ? 'text-line-through' : ''}>
                        {numberFormatter(platform.totalFollowers)}
                    </p>
                </td>
                <td className="text-center">
                    <p className={isStrikeTrough ? 'text-line-through' : ''}>
                        {numberFormatter(+calculateAvgReach(initialChildOffer?.offerItems, platform)?.toFixed(2))}
                    </p>
                </td>
                <td>
                    <div className={`d-flex justify-content-center ${isStrikeTrough ? 'text-line-through' : ''}`}>
                        {platform?.audiences.map((country, index) => (
                            <div key={Number(country?.id + index)} className="d-flex pl-2 align-items-center">
                                <Flag className="rounded" code={country?.metric} width={30} />
                                <div className="d-flex flex-column pl-2">
                                    <p className="text-primary font-weight-bold mb-0">{Math.round(country.value)}%</p>
                                    <p className="text-secondary mb-0">{numberFormatter(country.followers)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </td>
                <td className={`${isCharity ? 'd-none' : 'text-center'} ${isStrikeTrough ? 'text-line-through' : ''}`}>
                    <p className="euro-symbol">{getCpmValue(initialChildOffer.offerItems, platform)}</p>
                </td>
                <td className={`${isCharity ? 'd-none' : 'text-end'} ${isStrikeTrough ? 'text-line-through' : ''}`}>
                    <div className="d-flex align-items-center justify-content-end">
                        <p className="euro-symbol">
                            {priceSplitter(
                                Math.round(getChildOfferPricePerPlatform(initialChildOffer.offerItems, platform.id))
                            )}
                        </p>
                    </div>
                </td>
            </tr>
            {platform?.offerItems?.map((item, index) => {
                if (!item || !item.socialPlatform || !item.socialPlatform.id) {
                    return;
                }
                const defaultPosts = item?.nbPosts ?? 0;
                const postNumber = checkNumberPosts(
                    item.socialPlatform.id,
                    defaultPosts,
                    initialChildOffer?.offerItems ?? []
                );
                return (
                    <tr
                        key={Number(item.id + index)}
                        className={`position-relative
                        ${postNumber === 0 || !item.id ? 'text-line-through' : ''}
                        ${expended ? '' : 'd-none'}
                     `}
                    >
                        <td colSpan={2}>
                            <div className="d-flex align-items-center pl-2">
                                {isEditable ? (
                                    <span className="mb-0" onClick={() => deleteOfferItem(item.socialPlatform.id)}>
                                        <TrashIcon width="15" height="16" classes="cursor-pointer mr-2" />
                                    </span>
                                ) : null}
                                <CustomImageHandler
                                    altTag={item?.socialPlatform?.name ?? ''}
                                    photoPath={item?.socialPlatform?.path ?? ''}
                                />
                                <span className="px-2 mb-0">
                                    {t(`general.platforms.${item?.socialPlatform?.name}`)}
                                </span>
                            </div>
                        </td>
                        <td className="mb-0 delete-margin-bottom">
                            <div className="mr-3 ">
                                {isEditable ? (
                                    <Counter
                                        maxCount={item?.maxPosts}
                                        defaultCount={postNumber}
                                        setNumberItems={changeHandlerOfferItem}
                                        id={item.socialPlatform.id}
                                        itemId={item?.socialPlatform?.id}
                                    />
                                ) : (
                                    <span className="text-muted font-weight-bold mb-0 ">x {item?.nbPosts}</span>
                                )}
                            </div>
                        </td>
                        <td className={`${isCharity ? 'd-none' : ''}`}>
                            {isEditable && (
                                <div className="d-flex text-muted text-center">
                                    <p className="mr-2">{t('influencer.offer_management.modal.available')}</p>
                                    <p className="mb-0">{(item?.maxPosts ?? 0) - postNumber}</p>
                                </div>
                            )}
                        </td>
                        <td className={`${isCharity ? 'd-none' : ''}`}></td>
                        <td className={`${isCharity ? 'd-none' : 'd-flex justify-content-end'}`}>
                            <p className="text-muted mb-0 mt-2 euro-symbol">
                                {priceSplitter(Math.round((item?.singlePostPrice ?? 0) * postNumber))}
                            </p>
                        </td>
                    </tr>
                );
            })}
        </>
    );
};

export default ChildOfferPlatformItem;
